"use client"
import { useState, useEffect } from "react"
import { Icon } from "@/components/formatters/MXPIcon"
import { useScreenSize } from "../../utils/hooks/useScreenSize"

interface DropdownProps {
  options: { label: string; value: string }[] | string[]
  selectedFilters?: string[]
  onChange: (selectedOptions: string[]) => void
  label?: string
  multiSelect?: boolean
  displayResetAll?: boolean
  resetAllLabel?: string
  onResetAll?: () => void
  inputClasses?: string
  wrapperClasses?: string
  dropdownClasses?: string
}

const CHECKBOX_BASE =
  "min-w-[18px] min-h-[18px] cursor-pointer ml-auto appearance-none rounded border border-gray-m10 bg-transparent pointer-events-none"

const CHECKMARK_SVG =
  "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16'%3E%3Cpath fill='none' stroke='%23fff' stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M4 8l3 3 5-6'/%3E%3C/svg%3E\")"

const checkedStyle: React.CSSProperties = {
  backgroundColor: "#0063A3",
  borderColor: "#0063A3",
  backgroundImage: CHECKMARK_SVG,
  backgroundRepeat: "no-repeat",
  backgroundPosition: "center",
  backgroundSize: "70%",
}

export const Dropdown = ({
  options,
  onChange,
  label,
  selectedFilters = [],
  multiSelect = true,
  displayResetAll,
  resetAllLabel,
  onResetAll,
  inputClasses,
  wrapperClasses,
  dropdownClasses
}: DropdownProps) => {
  const [isOpen, setIsOpen] = useState(false)
  const [selectedOptions, setSelectedOptions] = useState(selectedFilters)

  const { width: _width } = useScreenSize()

  /* eslint-disable react-hooks/set-state-in-effect */
  useEffect(() => {
    setSelectedOptions(selectedFilters || [])
  }, [selectedFilters])
  /* eslint-enable react-hooks/set-state-in-effect */

  const handleOptionClick = (option: string) => {
    let updatedSelectedOptions

    if (multiSelect) {
      if (selectedOptions.includes(option)) {
        updatedSelectedOptions = selectedOptions.filter(o => o !== option)
      } else {
        updatedSelectedOptions = [...selectedOptions, option]
      }
    } else {
      updatedSelectedOptions = [option]
    }

    setSelectedOptions(updatedSelectedOptions)
    onChange(updatedSelectedOptions)
  }

  // Options aren't natively focusable, so clicking one triggers a browser
  // default "blur" on the currently-focused trigger before the click event
  // fires (mousedown -> blur -> mouseup -> click). That blur bubbles to the
  // wrapper's onBlur below and closes the menu (isOpen -> false, applying
  // pointer-events-none) right as the click is about to land, so the
  // selection intermittently fails to register (looks like a flicker).
  // Prevent the browser's default blur-on-mousedown for option rows so
  // focus stays put and the click reliably reaches handleOptionClick.
  const preventOptionBlur = (e: React.MouseEvent) => {
    e.preventDefault()
  }

  const handleClearAll = () => {
    setSelectedOptions([])
    onChange([])
  }

  const handleBlur = (e: React.FocusEvent<HTMLDivElement>) => {
    if (!e.currentTarget.contains(e.relatedTarget)) {
      setIsOpen(false)
    }
  }

  return (
    // text-left resets any inherited text-align (e.g. from a centered table
    // header cell) so wrapped trigger labels and options stay left-aligned.
    <div
      className={`relative text-left rounded-lg min-w-[185px] w-fit outline-none bg-white border border-gray-m2 shadow-sm max-lg:w-full max-lg:shadow-md ${isOpen ? "shadow-md" : ""} ${wrapperClasses} `}
      tabIndex={0}
      onBlur={handleBlur}
    >
      <div
        className={`flex gap-5 items-center py-[10px] px-4 rounded-lg cursor-pointer max-lg:justify-between ${inputClasses}`}
        onClick={() => setIsOpen(!isOpen)}
        tabIndex={0}
        data-testid="dropdown-trigger"
      >
        {label}
        <Icon type={"t2u-chevron-right"} rotateDeg="90" size={12} />
      </div>
      <div
        data-testid="dropdown-options"
        className={`mt-[6px] absolute bg-white z-10 w-full max-h-[300px] overflow-hidden flex flex-col rounded-lg shadow-md transition-all ${isOpen ? "visible opacity-100" : "invisible opacity-0 pointer-events-none"} ${dropdownClasses}`}
      >
        <ul className="list-none m-0 p-0 flex-1 overflow-y-auto flex flex-col max-sm:w-full">
          {options.map((option, index) => {
            const value = typeof option === "string" ? option : option.value
            const label = typeof option === "string" ? option : option.label

            return (
              <li
                key={`option-${value}-${index}`}
                className="cursor-pointer py-[10px] px-4 flex items-center justify-between list-none text-gray-m7 text-base font-normal leading-6 tracking-normal hover:bg-gray-m1"
                onMouseDown={preventOptionBlur}
                onClick={() => handleOptionClick(value)}
              >
                <span
                  className={
                    selectedOptions.includes(value)
                      ? "text-blue-trmb font-semibold"
                      : ""
                  }
                >
                  {label}
                </span>
                <input
                  type="checkbox"
                  className={CHECKBOX_BASE}
                  style={
                    selectedOptions.includes(value) ? checkedStyle : undefined
                  }
                  checked={selectedOptions.includes(value)}
                  onChange={() => handleOptionClick(value)}
                />
              </li>
            )
          })}
        </ul>
        {multiSelect && !displayResetAll ? (
          <div className="flex justify-between p-2 bg-white border-t border-gray-m2">
            <button
              className="border-none cursor-pointer text-sm font-bold leading-normal tracking-wide rounded uppercase bg-transparent text-gray-m3"
              onClick={handleClearAll}
            >
              Clear All
            </button>
          </div>
        ) : onResetAll ? (
          <div className="flex justify-between p-2 bg-white border-t border-gray-m2">
            <button
              className="border-none cursor-pointer text-sm font-bold leading-normal tracking-wide rounded uppercase bg-transparent text-gray-m3"
              onClick={onResetAll}
            >
              {resetAllLabel || "Reset All"}
            </button>
          </div>
        ) : null}
      </div>
    </div>
  )
}
